// PostBody.tsx — Safe renderer for legacy seed post bodies.
//
// Seed post bodies contain a limited, known set of markup:
//   - Line breaks:  <br/>  <br>  <br />
//   - Hashtag anchors: <a class="tag" href="...">#Text</a>
//   - No other markup is expected; any stray tags are stripped.
//
// Rules:
//   1. NO dangerouslySetInnerHTML — all rendering is via React nodes.
//   2. Split on <br> variants → render each segment separated by real <br />.
//   3. Within each segment, extract <a class="tag"…>#Tag</a> → styled <Tag> span.
//   4. Within the remaining plain text, http(s) URLs become real anchors
//      (1.24.00 C1) — they used to render as inert text you could not click.
//      The URL matcher lives in linkify.ts and is SHARED with bodyText.ts so
//      the rendered link and the report modal's plain-text twin can never
//      disagree about where a link ends.
//   4b. #hashtags and @mentions in plain text are in-app links: a hashtag opens the
//      search for that tag, a mention opens that member's profile (linkTokens).
//   5. Any OTHER residual <tag> / </tag> patterns are stripped from plain text.

import type { ReactNode } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import styled from 'styled-components';
import { splitOnLinks } from './linkify';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PostBodyProps {
  body: string;
  className?: string;
  /**
   * Render #hashtags and @mentions as plain coloured text instead of links. For a
   * PREVIEW of a post (the share sheet, an appearance sample): the reader is looking
   * at a picture of the post, and a tap there must not navigate away from what they
   * were doing. Outbound http(s) links stay links either way.
   */
  inert?: boolean;
}

// ─── Styled primitives ────────────────────────────────────────────────────────

const Wrap = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: 1.5;
  max-width: 68ch;
  min-width: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Tag = styled.span`
  color: ${({ theme }) => theme.color.accentCyan};
  font-weight: 500;
`;

// A #hashtag or an @mention: an in-app link (the hashtag search, or the profile).
const TokenLink = styled(Link)`
  color: ${({ theme }) => theme.color.accentCyan};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

// A link inside a body. `word-break` matters: a long URL in a narrow feed
// column would otherwise push the whole card wider than the grid column.
const BodyLink = styled.a`
  color: ${({ theme }) => theme.color.accentCyan};
  text-decoration: none;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Matches <a class="tag" href="...">...</a> (captures inner text).
const TAG_ANCHOR_RE = /<a\s[^>]*class=["']tag["'][^>]*>(.*?)<\/a>/gi;

// Matches any remaining HTML tag after we've extracted anchors.
const RESIDUAL_TAG_RE = /<[^>]+>/g;

/**
 * Render one run of non-hashtag text: URLs become anchors, everything else is
 * tag-stripped plain text.
 *
 * Order matters — links are tokenized BEFORE residual tags are stripped. Strip
 * first and `https://a.example/x<b>bold` collapses to `https://a.example/xbold`,
 * producing an anchor to a URL nobody wrote. Tokenizing first ends the link at
 * the `<` (see linkify.ts) and strips the stray tag out of the text run only.
 */
function renderTextRun(raw: string, keyPrefix: string, linkable: boolean): ReactNode[] {
  const nodes: ReactNode[] = [];
  splitOnLinks(raw).forEach((seg, i) => {
    if (seg.href) {
      nodes.push(
        // rel="nofollow" as well as noopener/noreferrer: these are
        // member-authored outbound links, and this platform does not lend its
        // ranking to whatever anyone pastes.
        <BodyLink
          key={`${keyPrefix}-link-${i}`}
          href={seg.href}
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          {seg.text}
        </BodyLink>,
      );
      return;
    }
    const text = stripTags(seg.text);
    if (text) nodes.push(...linkTokens(text, `${keyPrefix}-${i}`, linkable));
  });
  return nodes;
}

// A #hashtag or an @mention inside plain text. Both must start the text or follow
// whitespace or an opening bracket/quote, so an email address ("a@b.example") and a URL
// fragment never match. A handle may have dots between its parts ("@jordan.vale"); the
// server's mention parser uses the same shape (apps/social/signals.py _MENTION_RE).
const TOKEN_RE = /(^|[\s([{"'])(#[\p{L}\p{N}_]{2,50}|@[A-Za-z0-9_]+(?:\.[A-Za-z0-9_]+)*)/gu;

/**
 * Turn the #hashtags and @mentions in a plain-text run into links: a hashtag opens the
 * search for that tag (the same destination as the Trending panel), a mention opens
 * that member's profile. Everything else stays a plain string.
 */
function linkTokens(text: string, keyPrefix: string, linkable: boolean): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN_RE.exec(text)) !== null) {
    const lead = match[1] ?? '';
    const token = match[2] ?? '';
    const start = match.index + lead.length;
    if (start > last) nodes.push(text.slice(last, start));
    nodes.push(
      !linkable ? (
        <Tag key={`${keyPrefix}-t-${start}`}>{token}</Tag>
      ) : token.startsWith('#') ? (
        <TokenLink key={`${keyPrefix}-t-${start}`} to={`/explore?q=${encodeURIComponent(token)}`}>
          {token}
        </TokenLink>
      ) : (
        <TokenLink key={`${keyPrefix}-t-${start}`} to={`/profile/${token.slice(1)}`}>
          {token}
        </TokenLink>
      ),
    );
    last = start + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** Parse a single line segment into an array of React nodes (plain text + Tag spans + links). */
function parseLine(segment: string, lineKey: string, linkable: boolean): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Reset stateful regex.
  TAG_ANCHOR_RE.lastIndex = 0;

  while ((match = TAG_ANCHOR_RE.exec(segment)) !== null) {
    // Plain text before this anchor.
    if (match.index > lastIndex) {
      const raw = segment.slice(lastIndex, match.index);
      nodes.push(...renderTextRun(raw, `${lineKey}-${lastIndex}`, linkable));
    }
    // The hashtag inner text (e.g. "#SpeakFreely").
    const inner = match[1] ?? '';
    nodes.push(
      linkable && inner.startsWith('#') ? (
        <TokenLink
          key={`${lineKey}-tag-${match.index}`}
          to={`/explore?q=${encodeURIComponent(stripTags(inner))}`}
        >
          {inner}
        </TokenLink>
      ) : (
        <Tag key={`${lineKey}-tag-${match.index}`}>{inner}</Tag>
      ),
    );
    lastIndex = match.index + match[0].length;
  }

  // Trailing plain text after the last anchor.
  if (lastIndex < segment.length) {
    const raw = segment.slice(lastIndex);
    nodes.push(...renderTextRun(raw, `${lineKey}-${lastIndex}`, linkable));
  }

  return nodes;
}

/** Strip any residual <tag> / </tag> from a plain-text string. */
function stripTags(text: string): string {
  return text.replace(RESIDUAL_TAG_RE, '');
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PostBody({ body, className, inert = false }: PostBodyProps) {
  // In-app links need a router above them. A body rendered outside one (an isolated
  // preview, a unit test of this component) falls back to plain coloured text rather
  // than throwing, exactly as `inert` asks for explicitly.
  const inRouter = useInRouterContext();
  const linkable = inRouter && !inert;

  // No fast path for "body has no angle brackets": a plain body is exactly
  // where a pasted URL lives, and it has to be linkified like any other. The
  // general path below already renders a single plain run as a bare string,
  // so a markup-free body still produces no wrapper elements.

  // Split on <br/> / <br> / <br /> (case-insensitive).
  const lines = body.split(/<br\s*\/?>/i);

  const children: ReactNode[] = [];

  lines.forEach((line, lineIdx) => {
    if (lineIdx > 0) {
      // Insert a real line-break element between segments.
      children.push(<br key={`br-${lineIdx}`} />);
    }

    const lineKey = `line-${lineIdx}`;
    const parsed = parseLine(line, lineKey, linkable);

    if (parsed.length === 1 && typeof parsed[0] === 'string') {
      // Single plain-text node — push directly (no wrapper span needed).
      children.push(parsed[0]);
    } else if (parsed.length > 0) {
      children.push(<span key={lineKey}>{parsed}</span>);
    }
    // Empty segments (e.g. trailing <br/>) produce nothing — that's fine.
  });

  return <Wrap className={className}>{children}</Wrap>;
}
